import { useApolloClient } from "@apollo/client";
import { Menu, MenuItem } from "@mui/material";
import { Button, Modal } from "pepsico-ds";
import { useCallback, useState } from "react";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import { getProductDeleteLinks } from "../../graphql/queries/loadKeywordGroupById";
import ProductGroupModal from "./ProductGroupModal";
import ProductsTable from "./ProductsTable";

const getAttachmentNames = (linkedTo = []) => {
  const links = Array.isArray(linkedTo) ? linkedTo : [linkedTo];
  return [...new Set(links.map((link) => link?.attachedTo).filter(Boolean))].join(
    ", "
  );
};

const ProductsListView = ({
  products,
  loading,
  filterData,
  productGroups,
  productGroupMap,
  handlers,
  onAddSingleProduct,
  onImportFile,
  onViewProduct,
  onEditProduct,
  onDeleteProduct,
  onDeleteMultiple,
  onSaveGroup,
}) => {
  const client = useApolloClient();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState("");

  const handleAddProductClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddSingleProduct = () => {
    handleMenuClose();
    onAddSingleProduct();
  };

  const handleImportFile = () => {
    handleMenuClose();
    onImportFile();
  };

  const handleSelectionChange = useCallback((ids) => {
    setSelectedProductIds(ids);
  }, []);

  const handleBulkAddToGroup = () => {
    setShowGroupModal(true);
  };

  const handleSaveGroupFromBulk = (groupName, productIds) => {
    onSaveGroup(groupName, productIds);
    setSelectedProductIds([]);
  };

  const loadProductDeleteLinks = useCallback(
    async (id) => {
      const { data } = await client.query({
        query: getProductDeleteLinks,
        variables: { id },
        fetchPolicy: "network-only",
      });

      const product = data?.loadKeyWordGroupById;
      return {
        isLinked: Boolean(product?.isLinked),
        linkedTo: product?.linkedTo || [],
      };
    },
    [client]
  );

  const showLinkedProductMessage = useCallback((name, linkedTo) => {
    const attachedTo = getAttachmentNames(linkedTo);
    setDeleteBlockedMessage(
      `Product "${name}" is attached to ${attachedTo || "another item"} and cannot be deleted.`
    );
  }, []);

  const handleBulkDelete = async () => {
    try {
      const selectedProducts = await Promise.all(
        selectedProductIds.map(async (id) => {
          const product = products.find((item) => item.id === id);
          const linkDetails = await loadProductDeleteLinks(id);
          return { name: product?.name || id, ...linkDetails };
        })
      );
      const linkedProducts = selectedProducts.filter(
        ({ isLinked, linkedTo }) =>
          isLinked || (Array.isArray(linkedTo) ? linkedTo.length > 0 : linkedTo)
      );

      if (linkedProducts.length > 0) {
        setDeleteBlockedMessage(
          <div>
            <p>Some selected products cannot be deleted because they are linked:</p>
            <ul>
              {linkedProducts.map(({ name, linkedTo }) => (
                <li key={name}>
                  <strong>{name}</strong> —{" "}
                  {getAttachmentNames(linkedTo) || "Another item"}
                </li>
              ))}
            </ul>
            <p>Remove these products from the selection before deleting.</p>
          </div>
        );
        return;
      }

      setDeleteConfirmation({ type: "bulk", ids: selectedProductIds });
    } catch {
      setDeleteBlockedMessage(
        "Unable to verify whether the selected products are attached. Please try again."
      );
    }
  };

  const handleRequestDeleteProduct = useCallback(
    (product) => {
      if (product.isLinked) {
        showLinkedProductMessage(product.name, product.linkedTo);
        return;
      }

      setDeleteConfirmation({
        type: "single",
        id: product.id,
        name: product.name,
      });
    },
    [showLinkedProductMessage]
  );

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmation) return;
    if (deleteConfirmation.type === "bulk") {
      if (onDeleteMultiple) {
        onDeleteMultiple(deleteConfirmation.ids);
      }
      setSelectedProductIds([]);
    } else if (onDeleteProduct) {
      onDeleteProduct(deleteConfirmation.id, deleteConfirmation.name);
    }
    setDeleteConfirmation(null);
  };

  const selectedCount = selectedProductIds.length;

  const deleteConfirmationTitle =
    deleteConfirmation?.type === "bulk"
      ? `Delete ${deleteConfirmation.ids.length} selected product${
          deleteConfirmation.ids.length > 1 ? "s" : ""
        }?`
      : `Delete "${deleteConfirmation?.name}"?`;

  return (
    <div className="products-list-view">
      <div className="products-list-actions">
        <Button
          text="Add product"
          onClick={handleAddProductClick}
          size="medium"
          iconTrailing="arrow_drop_down"
          className="add-product-dropdown-btn"
        />
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleAddSingleProduct} sx={{ minWidth: "180px" }}>
            Add single product
          </MenuItem>
          <MenuItem onClick={handleImportFile} sx={{ minWidth: "180px" }}>
            Import file
          </MenuItem>
        </Menu>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-selection-count">
            {selectedCount} of {products.length} rows selected
          </span>
          <div className="bulk-action-buttons">
            <Button
              text="Delete"
              variant="secondary"
              size="small"
              onClick={handleBulkDelete}
            />
            <Button
              text="Add to product group"
              variant="primary"
              size="small"
              onClick={handleBulkAddToGroup}
            />
          </div>
        </div>
      )}

      <ProductsTable
        products={products}
        loading={loading}
        filterData={filterData}
        productGroups={productGroups}
        productGroupMap={productGroupMap}
        handleView={onViewProduct}
        handleEdit={onEditProduct}
        handleSoftDelete={handleRequestDeleteProduct}
        handleFilterChange={handlers.handleFilterChange}
        selectable={true}
        selectedProductIds={selectedProductIds}
        onSelectionChange={handleSelectionChange}
      />

      <ProductGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onSave={handleSaveGroupFromBulk}
        preSelectedProductIds={selectedProductIds}
      />

      <DeleteConfirmationModal
        isOpen={Boolean(deleteConfirmation)}
        title={deleteConfirmationTitle}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <Modal
        showModal={Boolean(deleteBlockedMessage)}
        title="Cannot Delete Product"
        text={deleteBlockedMessage}
        onCloseModal={() => setDeleteBlockedMessage("")}
        showPrimaryButton={true}
        primaryButtonProps={{
          size: "medium",
          text: "Cancel",
          variant: "secondary",
          onClick: () => setDeleteBlockedMessage(""),
        }}
        showSecondaryButton={false}
        showTertiaryButton={false}
        showLink={false}
      />
    </div>
  );
};

export default ProductsListView;
